// In-memory RPC stub mirroring the shape of the real Supabase RPCs.
// Seeded from a small fixed dataset; test-only.
let state;
export function resetMock() {
  state = {
    event: {
      id: 'e1',
      name: 'SPS 2026',
      code: 'T',
      admin_pin: '1234',
      gift_total: 1500,
      frozen: false,
      min_age: 15,
      max_age: 60,
    },
    staff: [
      { id: 's-a', name: 'A', role: 'admin', station: null, active: true },
      { id: 's-v', name: 'V', role: 'volunteer', station: 'M1', active: true },
    ],
    sessions: new Map(),
    families: new Map([
      [
        'F1',
        {
          family_key: 'F1',
          name: 'Doe',
          mandal: 'M1',
          eligible: true,
          collector_member_id: 'M1x',
          collector_name: 'John Doe',
          n_total: 2,
          n_band: 1,
        },
      ],
    ]),
    people: new Map([
      [
        'M1x',
        {
          member_id: 'M1x',
          family_key: 'F1',
          name: 'John Doe',
          in_band: true,
          age: 30,
        },
      ],
    ]),
    handovers: new Map(),
    attempts: [],
    imports: [],
    updated_at: new Date().toISOString(),
  };
}
resetMock();

const uuid = () => crypto.randomUUID();

export function seedMock(patch) {
  Object.assign(state, patch);
}

export async function mockRpc(fn, args) {
  const now = new Date().toISOString();
  switch (fn) {
    case 'sps_login': {
      if (args.p_code !== state.event.code) throw new Error('BAD_CODE');
      const st = state.staff.find((s) => s.name === args.p_name && s.active);
      if (!st) throw new Error('UNKNOWN_NAME');
      if (st.role === 'admin' && args.p_pin !== state.event.admin_pin) throw new Error('BAD_PIN');
      const token = uuid();
      state.sessions.set(token, { staff: st });
      return {
        token,
        staff_name: st.name,
        role: st.role,
        station: st.station,
        event_name: state.event.name,
        gift_total: state.event.gift_total,
        frozen: state.event.frozen,
        min_age: state.event.min_age,
        max_age: state.event.max_age,
      };
    }
    case 'sps_staff_list':
      return state.staff
        .filter((s) => s.active)
        .map(({ name, role, station }) => ({ name, role, station }));
    case 'sps_admin_staff_list': {
      const sess = state.sessions.get(args.p_token);
      if (!sess) throw new Error('NO_SESSION');
      if (sess.staff.role !== 'admin') throw new Error('ADMIN_ONLY');
      // Mirrors the real RPC: returns deactivated staff too, so the admin screen
      // can show them as inactive rather than having them vanish.
      return state.staff.map(({ id, name, role, station, active }) => ({
        id,
        name,
        role,
        station,
        active,
      }));
    }
    case 'sps_add_staff': {
      const sess = state.sessions.get(args.p_token);
      if (!sess || sess.staff.role !== 'admin') throw new Error('ADMIN_ONLY');
      const dup = state.staff.find((s) => s.name === args.p_name);
      if (dup) return { status: 'duplicate' };
      const st = {
        id: uuid(),
        name: args.p_name,
        role: args.p_role,
        station: args.p_station,
        active: true,
      };
      state.staff.push(st);
      return { status: 'ok', id: st.id };
    }
    case 'sps_deactivate_staff': {
      const sess = state.sessions.get(args.p_token);
      if (!sess || sess.staff.role !== 'admin') throw new Error('ADMIN_ONLY');
      const st = state.staff.find((s) => s.id === args.p_staff_id);
      if (!st) return { status: 'noop' };
      if (
        st.role === 'admin' &&
        st.active &&
        !state.staff.some((o) => o.role === 'admin' && o.active && o.id !== st.id)
      )
        return { status: 'last_admin' };
      st.active = false;
      return { status: 'ok' };
    }
    case 'sps_reactivate_staff': {
      const sess = state.sessions.get(args.p_token);
      if (!sess || sess.staff.role !== 'admin') throw new Error('ADMIN_ONLY');
      const st = state.staff.find((s) => s.id === args.p_staff_id);
      if (!st) return { status: 'noop' };
      st.active = true;
      return { status: 'ok' };
    }
    case 'sps_delete_staff': {
      const sess = state.sessions.get(args.p_token);
      if (!sess || sess.staff.role !== 'admin') throw new Error('ADMIN_ONLY');
      const st = state.staff.find((s) => s.id === args.p_staff_id);
      if (!st) return { status: 'noop' };
      if (st.id === sess.staff.id) return { status: 'self' };
      if (
        st.role === 'admin' &&
        !state.staff.some((o) => o.role === 'admin' && o.active && o.id !== st.id)
      )
        return { status: 'last_admin' };
      const handed = [...state.handovers.values()].filter((h) => h.staff_name === st.name).length;
      if (handed > 0) return { status: 'has_records', handovers: handed };
      state.staff = state.staff.filter((s) => s.id !== st.id);
      return { status: 'ok' };
    }
    case 'sps_conflicts': {
      const sess = state.sessions.get(args.p_token);
      if (!sess) throw new Error('NO_SESSION');
      if (sess.staff.role !== 'admin') throw new Error('ADMIN_ONLY');
      return [];
    }
    case 'sps_roster': {
      const sess = state.sessions.get(args.p_token);
      if (!sess) throw new Error('NO_SESSION');
      return {
        server_time: now,
        frozen: state.event.frozen,
        families: [...state.families.values()],
        people: [...state.people.values()],
        handovers: [...state.handovers.values()],
      };
    }
    case 'sps_handover': {
      const sess = state.sessions.get(args.p_token);
      if (!sess) throw new Error('NO_SESSION');
      if (state.event.frozen) return { status: 'frozen' };
      const dup = [...state.handovers.values()].find((h) => h.client_id === args.p_client_id);
      if (dup) return { status: 'duplicate', handover: dup };
      const fam = state.families.get(args.p_family_key);
      if (!fam) throw new Error('UNKNOWN_FAMILY');
      if (!fam.eligible) return { status: 'not_eligible' };
      const live = [...state.handovers.values()].find(
        (h) => h.family_key === args.p_family_key && !h.voided_at,
      );
      if (live) {
        state.attempts.push({
          family_key: args.p_family_key,
          client_id: args.p_client_id,
          staff_name: sess.staff.name,
          attempted_at: now,
          collected_name: args.p_collected_name,
        });
        return { status: 'taken', handover: live };
      }
      const h = {
        id: uuid(),
        family_key: args.p_family_key,
        client_id: args.p_client_id,
        collected_name: args.p_collected_name,
        staff_name: sess.staff.name,
        station: sess.staff.station,
        created_at: now,
      };
      state.handovers.set(h.id, h);
      return { status: 'ok', id: h.id };
    }
    case 'sps_void': {
      const sess = state.sessions.get(args.p_token);
      if (!sess || sess.staff.role !== 'admin') throw new Error('ADMIN_ONLY');
      const h = state.handovers.get(args.p_handover_id);
      if (!h) return { status: 'noop' };
      h.voided_at = now;
      h.voided_by = sess.staff.name;
      h.void_reason = args.p_reason;
      return { status: 'ok' };
    }
    case 'sps_freeze': {
      const sess = state.sessions.get(args.p_token);
      if (!sess || sess.staff.role !== 'admin') throw new Error('ADMIN_ONLY');
      state.event.frozen = !!args.p_frozen;
      return { status: 'ok', frozen: state.event.frozen };
    }
    case 'sps_import_history': {
      const sess = state.sessions.get(args.p_token);
      if (!sess) throw new Error('NO_SESSION');
      if (sess.staff.role !== 'admin') throw new Error('ADMIN_ONLY');
      return state.imports || [];
    }
    case 'sps_stats': {
      const handed = [...state.handovers.values()].filter((h) => !h.voided_at).length;
      return {
        gift_total: state.event.gift_total,
        families: state.families.size,
        handed,
        by_mandal: [],
        by_staff: [],
      };
    }
    default:
      throw new Error(`mock rpc not implemented: ${fn}`);
  }
}
